import { Component } from "@theme/component";
import { fetchConfig } from "@theme/utilities";
import { CartAddEvent, CartErrorEvent, CartGroupedSections, CartPendingEvent } from "@theme/events";

/**
 * Match & Save upsell card. Adds multiple product variants to the cart in a
 * single request and dispatches the theme's standard cart events so the cart
 * drawer (or cart page) picks up the update through existing channels.
 *
 * Click is wired declaratively from the Liquid block via
 *   on:click="/onAddToCartClick"
 * — component.js routes the event to `onAddToCartClick` on the closest
 * `Component` instance, so this binding survives Section Rendering API morphs
 * without manual rebinding.
 */
class MatchAndSaveComponent extends Component {
  #errorTimeout;

  /** @returns {Array<{id: number, quantity: number}>} */
  #getItems() {
    return (this.dataset.variantIds || "")
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((id) => Number.isFinite(id) && id > 0)
      .map((id) => ({ id, quantity: 1 }));
  }

  /**
   * Tag the cart with a `__bundle` note attribute so the bundle's variant IDs
   * appear under "Additional details" on the Shopify order. Uses a unique key
   * per bundle (`__bundle`, `__bundle 2`, …) so multiple bundles in the same
   * order don't overwrite each other.
   * @param {Array<{id: number}>} items
   */
  async #tagBundleOnCart(items) {
    const bundleTag = items.map((i) => i.id).join(",");
    if (!bundleTag) return;
    try {
      const cartRes = await fetch(`${FoxTheme.routes.cart_url}.js`, {
        headers: { Accept: "application/json" },
      });
      const cart = await cartRes.json();
      const existing = (cart && cart.attributes) || {};
      let key = "__bundle";
      if (Object.prototype.hasOwnProperty.call(existing, key)) {
        let i = 2;
        while (Object.prototype.hasOwnProperty.call(existing, `${key} ${i}`)) i++;
        key = `${key} ${i}`;
      }
      await fetch(
        FoxTheme.routes.cart_update_url,
        fetchConfig("json", { body: JSON.stringify({ attributes: { [key]: bundleTag } }) })
      );
    } catch (error) {
      console.error("[match-and-save] failed to tag bundle on cart", error);
    }
  }

  #gatherSectionIds() {
    const sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));
    return [...new Set(sections)];
  }

  #setLoading(isLoading) {
    const button = this.refs.addToCartButton;
    if (!(button instanceof HTMLElement)) return;
    button.classList.toggle("btn--loading", isLoading);
    const spinner = button.querySelector(".match-and-save__spinner");
    spinner?.classList.toggle("hidden", !isLoading);
    button.setAttribute("aria-busy", isLoading ? "true" : "false");
  }

  #showError(message) {
    const el = this.refs.errorMessage;
    if (this.#errorTimeout) {
      clearTimeout(this.#errorTimeout);
      this.#errorTimeout = undefined;
    }
    if (!(el instanceof HTMLElement)) {
      if (message) console.error("[match-and-save]", message);
      return;
    }
    if (!message) {
      el.classList.add("hidden");
      el.textContent = "";
      return;
    }
    el.textContent = message;
    el.classList.remove("hidden");
    this.#errorTimeout = window.setTimeout(() => {
      el.classList.add("hidden");
      el.textContent = "";
    }, 5000);
  }

  /**
   * @param {Event} event
   */
  onAddToCartClick = async (event) => {
    event?.preventDefault();

    if (this.dataset.unavailable === "true") return;

    const items = this.#getItems();
    if (!items.length) return;

    const button = this.refs.addToCartButton;
    if (button instanceof HTMLElement && button.getAttribute("aria-busy") === "true") return;

    this.#showError(null);
    this.#setLoading(true);

    const isDrawer = FoxTheme?.settings?.cartType === "drawer";

    // Non-drawer cart: submit and redirect to /cart so the user lands on the cart page.
    if (!isDrawer) {
      const formData = new FormData();
      items.forEach(({ id, quantity }, i) => {
        formData.append(`items[${i}][id]`, String(id));
        formData.append(`items[${i}][quantity]`, String(quantity));
      });
      try {
        const res = await fetch(FoxTheme.routes.cart_add_url, fetchConfig("javascript", { body: formData }));
        if (res.ok) {
          await this.#tagBundleOnCart(items);
          window.location.href = FoxTheme.routes.cart_url;
          return;
        }
        const parsed = await res.json().catch(() => null);
        this.#showError(parsed?.description || parsed?.message || "Could not add to cart.");
      } catch (error) {
        console.error(error);
        this.#showError("Something went wrong. Please try again.");
      } finally {
        this.#setLoading(false);
      }
      return;
    }

    const body = JSON.stringify({
      items,
      sections: this.#gatherSectionIds().join(","),
      sections_url: window.location.pathname,
    });

    document.dispatchEvent(new CartPendingEvent(this.id, { source: "match-and-save-component" }));

    try {
      const res = await fetch(FoxTheme.routes.cart_add_url, fetchConfig("json", { body }));
      const parsed = await res.json();

      if (parsed.status) {
        const message = parsed.description || parsed.message || "Could not add to cart.";
        this.dispatchEvent(new CartErrorEvent(this.id, message));
        this.#showError(message);
        return;
      }

      await this.#tagBundleOnCart(items);

      this.dispatchEvent(
        new CartAddEvent(parsed, this.id, {
          itemCount: items.reduce((n, i) => n + i.quantity, 0),
          source: "match-and-save-component",
          sections: parsed.sections,
        })
      );
    } catch (error) {
      console.error(error);
      const message = error instanceof Error ? error.message : String(error);
      this.dispatchEvent(new CartErrorEvent(this.id, message));
      this.#showError("Something went wrong. Please try again.");
    } finally {
      this.#setLoading(false);
    }
  };
}

if (!customElements.get("match-and-save-component")) {
  customElements.define("match-and-save-component", MatchAndSaveComponent);
}
